import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, CalendarCheck } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, PHONE_TEL } from "../../lib/site";
import { scrollToId } from "../../lib/scroll";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled || open ? "bg-[#0B0F17]/80 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo onClick={() => go("home")} />
        <nav className="hidden xl:flex items-center gap-1">
          {NAV.map(([id, label]) => (
            <button key={id} data-testid={`nav-link-${id}`} onClick={() => go(id)}
              className="nav-link relative px-3 py-2 text-sm text-slate-300 hover:text-white transition-colors">
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a href={PHONE_TEL} data-testid="nav-cta-call"
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white hover:border-[#D4AF37]/60 hover:bg-white/5 transition-colors">
            <Phone className="h-4 w-4 text-[#D4AF37]" /> Call Now
          </a>
          <a href={PHONE_TEL} aria-label="Call now" data-testid="nav-cta-call-mobile"
            className="sm:hidden grid h-10 w-10 place-items-center rounded-full border border-white/15 text-[#D4AF37]">
            <Phone className="h-4 w-4" />
          </a>
          <button onClick={() => go("contact")} data-testid="nav-cta-book"
            className="btn-gold hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold">
            <CalendarCheck className="h-4 w-4" /> Book a Visit
          </button>
          <button onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menu"
            className="xl:hidden grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden border-t border-white/10 bg-[#0B0F17]/95"
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 grid gap-1">
              {NAV.map(([id, label], i) => (
                <button key={id} onClick={() => go(id)} data-testid={`mobile-nav-link-${id}`}
                  className="flex items-baseline justify-between border-b border-white/5 py-3 text-left">
                  <span className="font-display text-2xl text-white">{label}</span>
                  <span className="font-mono-k text-xs text-slate-400">0{i + 1}</span>
                </button>
              ))}
              <button onClick={() => go("contact")} data-testid="mobile-nav-book"
                className="btn-gold mt-5 rounded-full py-3.5 font-semibold">Book a Visit</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
